import { useState } from "react";

import { FormularioCanal } from "./FormularioCanal";
import { TabelaCanais } from "./TabelaCanais";
import {
  useCanais,
  useCriarCanal,
  useEditarCanal,
} from "../../lib/config-alertas/hooks";
import type {
  Canal,
  CanalIn,
  CanalPatch,
} from "../../lib/config-alertas/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seção de Canais da Tela 6 (FE-6a). Orquestra (espelha `PaginaBancos`):
 *
 *  - `useCanais` (lista) + `useCriarCanal`/`useEditarCanal` (mutations);
 *  - `form: FormAberto` = qual formulário está aberto (criar | editar | nenhum);
 *  - "Novo canal" gated por `configurar_alertas`;
 *  - `<TabelaCanais>` + `<FormularioCanal>` (quando aberto);
 *  - no `onSuccess` da mutação o form fecha; o hook já invalida a lista (refetch).
 *
 * 🔴 `key` ESTÁVEL POR ALVO no `<FormularioCanal>` (`id` do canal em edição,
 * "novo-canal" na criação): força REMOUNT ao trocar de alvo. Sem isso, React reusa
 * a instância e os `useState(initializer)` NÃO re-rodam — o estado do form A
 * (inclusive a credencial 🔒 digitada) vazaria para o form B (Editar-A → Editar-B).
 *
 * SÓ tokens CPMH. Loading/erro como `PaginaBancos`.
 */

const ACAO_CONFIGURAR_ALERTAS = "configurar_alertas";

/** Qual formulário está aberto na seção. `null` = nenhum. */
type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; canal: Canal }
  | null;

export function SecaoCanais(): JSX.Element {
  const consulta = useCanais();
  const criar = useCriarCanal();
  const editar = useEditarCanal();
  const podeConfigurar = useTemPermissao(ACAO_CONFIGURAR_ALERTAS);

  const [form, setForm] = useState<FormAberto>(null);

  function salvarCriacao(corpo: CanalIn): void {
    criar.mutate(corpo, { onSuccess: () => setForm(null) });
  }

  function salvarEdicao(canal: Canal, corpo: CanalPatch): void {
    editar.mutate(
      { id: canal.id, corpo },
      { onSuccess: () => setForm(null) },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">Carregando canais…</p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar os canais.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const canais = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      {podeConfigurar && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Novo canal
          </button>
        </div>
      )}

      <TabelaCanais
        canais={canais}
        aoEditar={(canal) => setForm({ tipo: "editar", canal })}
      />

      {form?.tipo === "criar" && (
        <FormularioCanal
          key="novo-canal"
          aoCriar={salvarCriacao}
          aoEditar={() => undefined}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioCanal
          key={form.canal.id}
          canal={form.canal}
          aoCriar={() => undefined}
          aoEditar={(corpo) => salvarEdicao(form.canal, corpo)}
          aoCancelar={() => setForm(null)}
          salvando={editar.isPending}
          erro={editar.error}
        />
      )}
    </div>
  );
}
